<template>
  <div>
    <!-- Player Info Component -->
    <PlayerInfo
      :rider-data="rider_default_data"
      :genders="genders"
      :verification-status="verificationStatus"
    />

    <!-- Tab Navigation Component -->
    <TabNavigation
      :is-active-profile-tab="isActiveProfileTab"
      :is-active-event-highlights-tab="isActiveEventHighlightsTab"
      :is-active-activity-tab="isActiveActivityTab"
      :is-active-in-game-shop-tab="isActiveInGameShopTab"
      :is-active-vault-rewards-tab="isActiveVaultRewardsTab"
      :show-game-shop="
        rider_default_data?.user && !rider_default_data?.user?.isKid
      "
      @tab-changed="makeTabFunc"
    />

    <!-- Weight Submission Modal -->
    <WeightSubmissionModal
      :show="showWeightModal"
      :submission-type="currentSubmissionData?.type || 'pre_weight'"
      :event-data="currentSubmissionData || {}"
      @submit="handleWeightModalSubmit"
      @close="handleWeightModalClose"
    />

    <!-- Weight Submission Alerts - Show all pending submissions -->
    <WeightSubmissionAlert
      v-for="(submission, index) in weightSubmissions"
      :key="`weight-alert-${submission.event_id}-${submission.type}-${index}`"
      :show="true"
      :title="getAlertTitle(submission)"
      :start-time="submission.start_time_ms || submission.start_time"
      :end-time="
        submission.end_time_ms ||
        submission.end_time ||
        new Date(Date.now() + 24 * 60 * 60 * 1000)
      "
      :button-text="'Submit'"
      @submit="handleWeightSubmit(submission)"
      @expired="handleWeightExpired(submission)"
    />

    <!-- Only show VerificationAlert after data is loaded -->
    <VerificationAlert
      v-if="showVerificationAlert && isDataLoaded"
      :status="verificationStatus"
      :message="verificationMessage"
      :button-text="verificationButtonText"
      :button-route="verificationButtonRoute"
      @dismiss="handleAlertDismiss"
    />

    <section class="tab-content">
      <div class="container main-tab-container p-0">
        <div class="tab-content-wrpr">
          <div class="tab-content" id="nav-tabContent">
            <!-- Overview Component (Already existing) -->
            <OverView
              :is-active="isActiveProfileTab"
              :third-party-data="rider_default_data?.third_party_data"
              :user-data="rider_default_data?.user?.userData"
              :total-events="user_total_event_count"
            />

            <!-- Event Highlights Tab Content -->
            <EventHighlights
              :is-active="isActiveEventHighlightsTab"
              :gems-balance="userGems"
              @buy-gems="handleBuyGems"
            />

            <!-- Activities Tab Content -->
            <div
              class="tab-pane fade"
              :class="{ 'show active': isActiveActivityTab }"
              id="nav-activities"
              role="tabpanel"
              aria-labelledby="nav-activities-tab"
              tabindex="1"
            >
              <!-- When not showing detail -->
              <div v-if="!showDetail">
                <!-- Power Curves Component -->
                <PowerCurves
                  :chart-data-default="chartDataDefault"
                  :chart-options="chartOptions"
                  :chart-data-weight-per-kg="chartDataWeightPerKg"
                  :chart-options-weight-per-kg="chartOptionsWeightPerKg"
                  :first-graph-date="firstGraphDate"
                  :second-graph-date="secondGraphDate"
                  :tab-title="tabTitle"
                  @tab-selected="selectTab"
                />

                <!-- Activity Filters Component -->
                <ActivityFilters
                  :type="type"
                  :sortDate="sortDate"
                  @update:type="(val) => (type = val)"
                  @update:sortDate="(val) => (sortDate = val)"
                />

                <!-- Activity List Component -->
                <ActivityList
                  v-if="activities.results.length > 0"
                  :activities="activities"
                  :genders="genders"
                  :rider-data="rider_default_data"
                  :graph-file-ids="graphFileIds"
                  :finished="finished"
                  @view-activity="getSingleActivity"
                  @download-file="downloadFile"
                  @add-to-graph="addToGraph"
                  @load-more="getAllActivities"
                />

                <div v-else>
                  <div class="msg-txt">
                    <p>- All your activities will be displayed here -</p>
                  </div>
                </div>
              </div>

              <!-- When showing detail -->
              <ActivityDetail
                v-if="showDetail"
                :single-ride-data="single_ride_data"
                :rider-data="rider_default_data"
                :genders="genders"
                :selected-date="selectedDate"
                :selected-file-id="selectedFileId"
                :chart-options-elevation="chartOptionsElevation"
                :chart-data-elevation="chartDataElevation"
                :chart-options-power="chartOptionsPower"
                :chart-data-power="chartDataPower"
                :chart-options-speed="chartOptionsSpeed"
                :chart-data-speed="chartDataSpeed"
                :chart-options-heart-rate="chartOptionsHeartRate"
                :chart-data-heart-rate="chartDataHeartRate"
                :chart-options-cadence="chartOptionsCadence"
                :chart-data-cadence="chartDataCadence"
                :hover-elevation-date="hoverElevationDate"
                :hover-elevation-time="hoverElevationTime"
                :hover-elevation="hoverElevation"
                :hover-power="hoverPower"
                :hover-speed="hoverSpeed"
                :hover-heart-rate="hoverHeartRate"
                :hover-cadence="hoverCadence"
                :avg-power="avgPower"
                :min-power="minPower"
                :max-power="maxPower"
                :avg-speed="avgSpeed"
                :max-speed="maxSpeed"
                :avg-heart-rate="avgHeartRate"
                :max-heart-rate="maxHeartRate"
                :min-heart-rate="minHeartRate"
                :avg-cadence="avgCadence"
                :max-cadence="maxCadence"
                @hide-detail="hideDetail"
                @download-file="downloadFile"
              />
            </div>

            <!-- Game Shop Component (Already existing) -->
            <GameShop
              v-if="
                rider_default_data?.user && !rider_default_data?.user?.isKid
              "
              ref="gameShop"
              :is-active="isActiveInGameShopTab"
              :user-gems="
                rider_default_data?.user?.userData['data']['PlayerData'][
                  'PlayerPersonalStruct'
                ]['Gems']
              "
              :user-coins="
                rider_default_data?.user?.userData['data']['PlayerData'][
                  'PlayerPersonalStruct'
                ]['Coins']
              "
            />

            <!-- Vault Rewards Component -->
            <VaultRewards
              :is-active="isActiveVaultRewardsTab"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// Import components
import OverView from "@/components/rider/OverView.vue";
import EventHighlights from "@/views/user/EventHighlights.vue";
import GameShop from "@/components/game_shop/GameShop";
import PlayerInfo from "@/components/player/PlayerInfo.vue";
import TabNavigation from "@/components/player/TabNavigation.vue";
import PowerCurves from "@/components/player/PowerCurves.vue";
import ActivityFilters from "@/components/player/ActivityFilters.vue";
import ActivityList from "@/components/player/ActivityList.vue";
import ActivityDetail from "@/components/player/ActivityDetail.vue";
import VaultRewards from "@/components/player/VaultRewards.vue";
import VerificationAlert from "@/components/src-event/VerificationAlert.vue";
import WeightSubmissionAlert from "@/components/src-event/WeightSubmissionAlert.vue";
import WeightSubmissionModal from "@/components/src-event/WeightSubmissionModal.vue";
// Import mixins and services
import chartMixin from "@/mixins/chartMixin";
import activityService from "@/services/activity-service";

// Top-level tabs: the id of each tab's <a> in TabNavigation, the route it owns,
// and whether its content uses the wide (.container-fluid) layout.
const TABS = {
  profile: {
    navId: "nav-home-tab",
    route: { path: "/user/activities", hash: "#profile" },
    fluid: false,
  },
  highlights: {
    navId: "nav-event-highlights-tab",
    route: { path: "/user/activities", hash: "#event-highlights" },
    fluid: false,
  },
  activities: {
    navId: "nav-contact-tab3",
    route: { path: "/user/activities", hash: "#activities" },
    fluid: true,
  },
  inGameShop: {
    navId: "nav-in-game-shop-tab3",
    route: { path: "/user/activities", hash: "#in-game-shop" },
    fluid: true,
    kidsBlocked: true,
  },
  vaultRewards: {
    navId: "nav-vault-rewards-tab3",
    route: { path: "/vault-rewards" },
    fluid: true,
  },
};
const tabNames = Object.keys(TABS);
const tabByNavId = (navId) => tabNames.find((n) => TABS[n].navId === navId);
const tabByHash = (hash) => tabNames.find((n) => TABS[n].route.hash === hash);

export default {
  name: "PlayerActivities",
  head: {
    title: {
      inner: "Dashboard",
    },
    link: [
      {
        rel: "stylesheet",
        href: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css",
      },
      { rel: "stylesheet", href: "/assets/css/activities/style.css" },
    ],
  },
  mixins: [chartMixin],
  components: {
    GameShop,
    OverView,
    EventHighlights,
    PlayerInfo,
    TabNavigation,
    PowerCurves,
    ActivityFilters,
    ActivityList,
    ActivityDetail,
    VaultRewards,
    VerificationAlert,
    WeightSubmissionAlert,
    WeightSubmissionModal,
  },
  data() {
    return {
      // Data loading state
      isDataLoaded: false,

      // Verification state
      verificationStatus: "",
      verificationMessage: "",
      verificationButtonText: "",
      verificationButtonRoute: "",
      showVerificationAlert: false,

      // Weight submission state
      weightSubmissions: [], // Array of all pending weight submissions

      // Weight submission modal state
      showWeightModal: false,
      currentSubmissionData: null,

      // Activity state
      selectedDate: "",
      selectedFileId: "",
      showDetail: false,
      genders: ["MEN", "FEMALE", "Both"],
      graphFileIds: [],

      // Pagination state
      page: 1,
      type: "",
      finished: false,
      sortDate: "DESC",

      // Tab state
      isActiveProfileTab: true,
      isActiveEventHighlightsTab: false,
      isActiveActivityTab: false,
      isActiveInGameShopTab: false,
      isActiveVaultRewardsTab: false,
      defaultVerificationText:
        "If you have completed a Power passport test and wish to submit this to our verification team for consideration to be approved for premium racing on MyWhoosh, please submit via the verify button here. If you are already racing SRC, or have not done and filmed a power passport test to submit, there is no need to submit any data here. Please see the MyWhoosh esport ruleset for information regarding the power passport test.",
    };
  },
  computed: {
    // Rename the mapGetters to avoid naming conflicts
    ...mapGetters("user", {
      activities: "activities",
      compare_data: "compare_data",
      single_ride_data: "single_ride_data",
      rider_default_data: "rider_default_data",
      user_total_event_count: "user_total_event_count",
      verification_status: "verification_status",
    }),
    userGems() {
      return (
        Number(
          this.rider_default_data?.user?.userData?.data?.PlayerData
            ?.PlayerPersonalStruct?.Gems
        ) || 0
      );
    },
  },
  async created() {
    this.showTab();
  },
  async mounted() {
    await this.getAllActivities();
    await this.getDefaultData();
  },
  methods: {
    getAlertTitle(submission) {
      const typeText = submission.type === "pre_weight" ? "Pre" : "Post";
      return `${typeText}-Weight Submission: ${submission.event_name}`;
    },

    handleAlertDismiss: function () {
      this.showVerificationAlert = false;
    },

    // Weight submission methods
    handleWeightSubmit(submission) {
      this.currentSubmissionData = submission;
      this.showWeightModal = true;
    },

    async handleWeightModalSubmit(submissionData) {
      try {
        // Call the Vuex action to submit weight
        const response = await this.$store.dispatch(
          "user/submitWeight",
          submissionData
        );

        global.app.$toastr.success(response?.message);

        // Remove the submitted item from the weightSubmissions array
        this.weightSubmissions = this.weightSubmissions.filter(
          (sub) =>
            !(
              sub.event_id === submissionData.event_id &&
              sub.type === submissionData.type
            )
        );

        // Close modal
        this.showWeightModal = false;
        this.currentSubmissionData = null;

        // Refresh the user data to get updated weight submissions
        await this.getDefaultData();
      } catch (error) {
        console.error("Weight submission error:", error);

        if (global.app && global.app.$toastr) {
          global.app.$toastr.error(
            "An unexpected error occurred. Please try again."
          );
        }
      }
    },

    handleWeightModalClose() {
      this.showWeightModal = false;
      this.currentSubmissionData = null;
    },

    handleWeightExpired(submission) {
      // Remove the expired submission from the array
      this.weightSubmissions = this.weightSubmissions.filter(
        (sub) =>
          !(
            sub.event_id === submission.event_id && sub.type === submission.type
          )
      );

      // Show expired message
      const typeText = submission.type === "pre_weight" ? "Pre" : "Post";
      const message = `${typeText}-weight submission deadline for "${submission.event_name}" has passed.`;

      if (global.app && global.app.$toastr) {
        global.app.$toastr.warning(message);
      }
    },

    updateWeightSubmissionState(riderData) {
      // The backend gates the submission window using Unix epochs (timezone-safe),
      // so trust whatever it returns. Previously we re-checked start_time here using
      // browser-local Date parsing of a naive 'Y-m-d H:i' string, which made the
      // popup show or hide inconsistently for users outside Asia/Dubai.
      this.weightSubmissions = riderData?.user?.weight_submission_data || [];

      if (this.weightSubmissions.length > 0) {
        this.currentWeightSubmission = this.weightSubmissions[0];
      } else {
        this.currentWeightSubmission = null;
      }
    },

    updateVerificationState(riderData) {
      const newStatus =
        riderData?.verification_status ||
        this.verification_status ||
        "not_submitted";

      // Clear localStorage when status changes
      // if (this.verificationStatus && this.verificationStatus !== newStatus) {
      //   const possibleStatuses = [
      //     "pending",
      //     "approved",
      //     "rejected",
      //     "not submitted",
      //   ];
      //   possibleStatuses.forEach((statusType) => {
      //     try {
      //       // localStorage.removeItem(
      //       //   "verification_alert_dismissed_" + statusType
      //       // );
      //     } catch (error) {
      //       // Silent error handling
      //     }
      //   });
      // }

      this.verificationStatus = newStatus;

      // Set appropriate message and button based on status
      switch (newStatus) {
        case "pending":
          this.verificationMessage =
            "Your verification request is under review. You will be notified once it is complete.";
          this.verificationButtonText = "Got it";
          this.verificationButtonRoute = "#";
          this.showVerificationAlert = true; // Show alert for not_submitted status
          break;
        case "rejected":
          this.verificationMessage =
            "Your verification was rejected. Please review the feedback and resubmit your verification.";
          this.verificationButtonText = "Resubmit Verification";
          this.verificationButtonRoute = "/pre-verification-form";
          this.showVerificationAlert = true; // Show alert for not_submitted status
          break;
        case "approved":
          this.verificationMessage =
            "Congratulations! Your verification has been approved. You can now participate in races.";
          this.verificationButtonText = "Continue";
          this.verificationButtonRoute = "/user/activities#profile";
          this.showVerificationAlert = false; // Hide alert for approved status
          break;
        case "not_submitted":
          this.verificationMessage = this.defaultVerificationText;
          this.verificationButtonText = "Verify";
          this.verificationButtonRoute = "/pre-verification-form";
          this.showVerificationAlert = false; // Hide alert for not_submitted status
          break;
        default:
          this.verificationMessage = this.defaultVerificationText;
          this.verificationButtonText = "Verify";
          this.verificationButtonRoute = "/pre-verification-form";
          this.showVerificationAlert = false; // Hide alert for default/unknown status
      }
    },

    // Reflect the active tab across the per-tab `isActive*` flags consumed by
    // <TabNavigation> and the tab-pane components.
    setActiveTab(name) {
      this.isActiveProfileTab = name === "profile";
      this.isActiveEventHighlightsTab = name === "highlights";
      this.isActiveActivityTab = name === "activities";
      this.isActiveInGameShopTab = name === "inGameShop";
      this.isActiveVaultRewardsTab = name === "vaultRewards";
    },

    switchTab(name) {
      const tab = TABS[name];
      if (!tab) return;
      if (tab.kidsBlocked && this.rider_default_data?.user?.isKid) return;

      const tabClass = document.querySelector(".main-tab-container");
      if (tabClass) {
        tabClass.classList.toggle("container-fluid", !!tab.fluid);
        tabClass.classList.toggle("container", !tab.fluid);
      }
      this.setActiveTab(name);
      this.safeNavigate({ ...tab.route });
    },

    // EventHighlights "Buy Gems" → land on the In-Game Shop tab AND open the
    // PaymentModal directly on the gems tab (index 1) so the user doesn't have
    // to hunt for the "+" topup button.
    handleBuyGems() {
      this.switchTab("inGameShop");
      this.$nextTick(() => {
        const gs = this.$refs.gameShop;
        if (gs && typeof gs.activateChildTab === "function") {
          gs.activateChildTab(1);
        }
      });
    },

    makeTabFunc(evt) {
      const name = tabByNavId(evt.target.attributes.id.value);
      if (name) this.switchTab(name);
    },

    showTab() {
      const hash = window.location.hash;
      const path = this.$route?.path;
      const isKid = this.rider_default_data?.user?.isKid;

      const name =
        path === "/vault-rewards" || hash === "#vault-rewards"
          ? "vaultRewards"
          : tabByHash(hash) || "profile";

      if (TABS[name].kidsBlocked && isKid) {
        // kids can't open the in-game shop — bounce them to the profile tab
        this.$router.replace({ hash: "#profile" });
        return;
      }

      this.setActiveTab(name);
      document.getElementById(TABS[name].navId)?.click();
    },

    addToGraph(evt, fileId) {
      if (this.graphFileIds.indexOf(fileId) === -1) {
        evt.target.classList.add("active");
        this.graphFileIds.push(fileId);
      } else {
        evt.target.classList.remove("active");
        this.graphFileIds.splice(this.graphFileIds.indexOf(fileId), 1);
      }
    },

    async getDefaultData() {
      await activityService.getDefaultData(this.$store, (data) => {
        const powerData = data?.overall_average_graph?.power;
        const powerWeightData = data?.overall_average_graph?.power_weight;
        this.updateChartData(powerData, powerWeightData);

        this.isDataLoaded = true;
        this.updateVerificationState(data);
        this.updateWeightSubmissionState(data);
      });
    },

    async getAllActivities() {
      const result = await activityService.getAllActivities(this.$store, {
        type: this.type,
        page: this.page,
        sortDate: this.sortDate,
      });

      if (result.success) {
        if (result.isLastPage) {
          this.finished = true;
        } else {
          this.page++;
        }
      }
    },

    async downloadFile(fileId) {
      await activityService.downloadFile(this.$store, fileId);
    },

    async getSingleActivity(fileId, fileDate) {
      const result = await activityService.getSingleActivity(
        this.$store,
        fileId,
        (data) => {
          const success = this.processActivityData(data, fileId, fileDate);
          if (success) {
            this.showDetail = true;
            return { success: true };
          }
          return { success: false };
        }
      );

      if (!result.success) {
        global.app.$toastr.error(
          result.message || "Unable to fetch activity detail."
        );
      }
    },

    hideDetail() {
      this.showDetail = false;
    },

    safeNavigate(route) {
      if (this.$route.path + this.$route.hash !== route) {
        this.$router.push(route).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            console.error("Navigation error:", err);
          }
        });
      }
    },
  },
  watch: {
    $route() {
      this.showTab();
      this.showDetail = false;
    },

    async graphFileIds() {
      this.resetChartData();

      if (this.graphFileIds.length > 2) {
        this.graphFileIds.shift();
        Array.from(
          document.querySelectorAll(".input_compare")
        )[0].classList.remove("active");
      }

      if (this.graphFileIds.length === 0) {
        this.getDefaultData();
      }

      if (this.graphFileIds.length > 0 && this.graphFileIds.length <= 2) {
        await activityService.getCompareData(this.$store, this.graphFileIds);
      }
    },

    type() {
      this.finished = false;
      this.page = 1;
      this.getAllActivities();
    },

    sortDate() {
      this.finished = false;
      this.page = 1;
      this.getAllActivities();
    },
  },
};
</script>

<style scoped>
@media (min-width: 1920px) {
  .container {
    max-width: 1600px;
    margin: 0 auto;
  }
}
/* Enhanced alert styling based on status */
.weight-submission-banner[data-status="rejected"] {
  background: linear-gradient(135deg, #ffe6e6, #ffcccc);
  border-color: #ff6b6b;
}

.weight-submission-banner[data-status="not_submitted"] {
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  border-color: #e17055;
}
</style>
